import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n, useT } from '../../i18n';
import { Icon } from '../chrome/Icon';
import { useEntitlements, useStartTrial } from '../../data/queries';
import {
  TIER_META, FEATURE_ROWS, ALWAYS_INCLUDED, MONTHLY_TIERS, ONE_TIME_MONTHS,
  tierPrice, lowerTierFor, isUpgrade, oneTimeFor, oneTimeSavings, lifetimePrice, currencySymbol,
} from '../../data/entitlements';
import { useBilling, type PaywallReason } from '../../billing/BillingProvider';
import { billingMode, paddleMonthlyPriceIds, paddleLifetimePriceIds } from '../../billing/billingMode';
import { fetchRealPrices, type RealPrice } from '../../billing/paddle';
import type { SubscriptionTier, TierLimits } from '../../data/dto';

interface PaywallSheetProps {
  open: boolean;
  onClose: () => void;
  reason?: PaywallReason | null;
}

type MonthlyTier = (typeof MONTHLY_TIERS)[number];

const BASE = import.meta.env.BASE_URL;
/** Selected tier's scene — "Family" shows parents and baby, "Village" the
 *  wider circle the plan is named for. Both source images carry a baked-in
 *  fade to the surface color so they blend into the page below. */
const HERO_IMAGE: Record<MonthlyTier, string> = {
  premium: `${BASE}paywall/paywall_family.jpg`,
  ultra: `${BASE}paywall/paywall_village.jpg`,
};
/** Height of the photo + color backdrop, covering the close button, plan
 *  copy, and tier toggle — the point where the plain surface color takes
 *  over, matched to the rest of the page's usual spacing rhythm. */
const HERO_HEIGHT = 320;

function reasonHeadline(reason: PaywallReason | null | undefined, t: (k: string, f: string) => string): string | null {
  switch (reason?.feature) {
    case 'babies':           return t('billing.reason.babies', 'You\'ve reached your baby limit.');
    case 'members_per_baby': return t('billing.reason.sharing', 'You\'ve reached your sharing limit for this baby.');
    case 'images':           return t('billing.reason.images', 'You\'ve reached your photo limit for this baby.');
    case 'videos':           return t('billing.reason.videos', 'You\'ve reached your video limit for this baby.');
    case 'activity_ai':      return t('billing.reason.ai', 'AI activity ideas are a paid feature.');
    default:                 return null;
  }
}

/** Full-screen plan picker. Bottom offers BOTH order options for the selected
 *  tier: a monthly subscription and a one-time 24-month pass (with savings). */
export function PaywallSheet({ open, onClose, reason }: PaywallSheetProps) {
  const t = useT();
  const { locale } = useI18n();
  const ent = useEntitlements(open);
  const startTrial = useStartTrial();
  const { openCheckout } = useBilling();

  const currentTier: SubscriptionTier = ent.data?.tier ?? 'free';
  const allTiers = ent.data?.all_tiers;
  const trial = ent.data?.trial;
  const isTrialing = trial?.active ?? false;

  // While trialing, default to the trialed tier so the user can convert it to
  // paid; otherwise default to the most relevant upgrade above the current plan.
  const defaultTier = (cur: SubscriptionTier): MonthlyTier =>
    isTrialing ? (cur === 'ultra' ? 'ultra' : 'premium') : (cur === 'premium' ? 'ultra' : 'premium');
  const [selected, setSelected] = useState<MonthlyTier>(defaultTier(currentTier));
  const [busy, setBusy] = useState<string | null>(null);
  // Real, localized Paddle prices keyed by price id — TIER_META's amounts are
  // indicative headline estimates only; Paddle determines the buyer's actual
  // tax-inclusive total, which routinely differs from them (see billing/paddle.ts).
  const [realPrices, setRealPrices] = useState<Record<string, RealPrice>>({});

  useEffect(() => {
    if (open) setSelected(defaultTier(currentTier));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentTier, isTrialing]);

  useEffect(() => {
    if (!open || billingMode !== 'paddle') return;
    const ids = [
      paddleMonthlyPriceIds.premium, paddleMonthlyPriceIds.ultra,
      paddleLifetimePriceIds.premium, paddleLifetimePriceIds.ultra,
    ].filter((id): id is string => Boolean(id));
    let cancelled = false;
    fetchRealPrices(ids).then((prices) => { if (!cancelled) setRealPrices(prices); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const lower = lowerTierFor(selected);
  const headline = reasonHeadline(reason, t);
  // Orderable when it's an upgrade, OR when converting the active trial to a paid
  // plan of the tier being trialed (same tier as current).
  const canOrder = isUpgrade(currentTier, selected) || (isTrialing && selected === currentTier);
  const selectedName = t(TIER_META[selected].nameKey, TIER_META[selected].nameFallback);

  const oneTime = oneTimeFor(selected);
  const monthlyPriceId = paddleMonthlyPriceIds[selected];
  const lifetimePriceId = paddleLifetimePriceIds[selected];
  const realMonthly = monthlyPriceId ? realPrices[monthlyPriceId] : undefined;
  const realLifetime = lifetimePriceId ? realPrices[lifetimePriceId] : undefined;

  const monthlyPriceText = realMonthly?.formatted ?? tierPrice(selected, locale);
  const lifetimePriceText = realLifetime?.formatted ?? (oneTime ? lifetimePrice(oneTime, locale) : '');

  // Real prices give exact savings math; fall back to the static estimate
  // when Paddle hasn't returned pricing yet (or isn't configured — mock mode).
  const savings = (() => {
    if (!oneTime) return null;
    if (!realMonthly || !realLifetime) return oneTimeSavings(oneTime, locale);
    const total = realMonthly.amount * ONE_TIME_MONTHS;
    const save = Math.max(0, Math.round((total - realLifetime.amount) * 100) / 100);
    const pct = total > 0 ? Math.round((save / total) * 100) : 0;
    return { months: ONE_TIME_MONTHS, save, pct };
  })();
  const savingsText = (() => {
    if (!savings || savings.save <= 0) return null;
    if (realMonthly) {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency', currency: realMonthly.currencyCode, maximumFractionDigits: 0,
        }).format(savings.save);
      } catch {
        // fall through to the guessed-symbol format below
      }
    }
    return `${currencySymbol(locale)}${savings.save}`;
  })();

  const buy = async (tier: SubscriptionTier, lifetime = false) => {
    const key = lifetime ? `${tier}:lt` : tier;
    setBusy(key);
    try {
      await openCheckout(tier, { lifetime });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[billing] checkout failed', e);
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;

  // Portal to <body> at z-[60] so the full-screen paywall sits above any open sheet.
  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-bg text-ink" role="dialog" aria-modal="true" aria-label={t('billing.title', 'Choose your plan')}>
      {/* ── Scrollable content ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-md">
          {/* Hero: selected tier's scene as a background, with a color wash
              (keyed to the accent) so the close button, plan copy, and tier
              toggle stay legible over a busy photo. Both the wash and the
              photo's own baked-in fade hand off to the plain surface color
              right around where the toggle ends. */}
          <div className="relative overflow-hidden" style={{ height: HERO_HEIGHT }}>
            <img key={selected} src={HERO_IMAGE[selected]} alt="" className="absolute inset-0 size-full object-cover" />
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: 'linear-gradient(to bottom, transparent 0%, transparent 35%, var(--color-bg) 92%)' }}
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'linear-gradient(to bottom, color-mix(in oklch, var(--color-accent) 88%, transparent) 0%, color-mix(in oklch, var(--color-accent) 55%, transparent) 40%, transparent 85%)',
              }}
            />

            <div className="relative flex flex-col items-center px-5 pt-4 text-center">
              <div className="flex w-full justify-end">
                <button
                  onClick={onClose}
                  aria-label={t('common.close', 'Close')}
                  className="flex size-7 items-center justify-center rounded-full bg-white/25 text-white"
                >
                  <Icon name="close" size={16} />
                </button>
              </div>

              {/* Brand + current plan */}
              <span className="mt-1 flex size-12 items-center justify-center rounded-2xl bg-white text-accent">
                <Icon name="trophy" size={24} />
              </span>
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/22 px-3 py-1 text-[12px] font-semibold text-white">
                {t('billing.yourPlan', 'Your plan')}: {t(TIER_META[currentTier].nameKey, TIER_META[currentTier].nameFallback)}
              </p>
              <h2 className="mt-3 font-serif text-2xl tracking-tight text-white">
                {t('billing.get', 'Get {plan}').replace('{plan}', selectedName)}
              </h2>
              {headline ? (
                <p className="mt-1 text-[13px] font-semibold text-white">{headline}</p>
              ) : isTrialing ? (
                <p className="mt-1 text-[13px] font-semibold text-white">
                  {t('billing.trialActive', 'Free trial active — subscribe now to keep {plan} when it ends.').replace('{plan}', selectedName)}
                </p>
              ) : (
                <p className="mt-1 text-sm text-white/85">{t(TIER_META[selected].taglineKey, TIER_META[selected].taglineFallback)}</p>
              )}

              {/* Toggle Family | Village */}
              <div className="mt-5 grid w-full grid-cols-2 gap-1 rounded-2xl bg-white/55 p-1 backdrop-blur-md">
                {MONTHLY_TIERS.map((tier) => {
                  const on = selected === tier;
                  return (
                    <button
                      key={tier}
                      onClick={() => setSelected(tier)}
                      aria-pressed={on}
                      className={['min-h-10 rounded-xl text-[14px] font-semibold transition-colors', on ? 'bg-bg text-ink shadow-sm' : 'text-ink-2'].join(' ')}
                    >
                      {t(TIER_META[tier].nameKey, TIER_META[tier].nameFallback)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="px-5 pb-6">
          {/* Comparison: lower vs selected */}
          {allTiers && (
            <div className="mt-4 overflow-hidden rounded-2xl border border-line bg-surface">
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 px-4 py-2.5 text-[12px] font-semibold text-ink-3">
                <span>{t('billing.features', 'Features')}</span>
                <span className="w-16 text-center">{t(TIER_META[lower].nameKey, TIER_META[lower].nameFallback)}</span>
                <span className="w-16 text-center text-accent">{selectedName}</span>
              </div>
              {FEATURE_ROWS.map((row) => (
                <div key={row.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 border-t border-line px-4 py-3 text-[13.5px]">
                  <span className="text-ink">{t(row.labelKey, row.labelFallback)}</span>
                  <span className="w-16 text-center text-ink-2">{cell(allTiers[lower], row.key)}</span>
                  <span className="w-16 text-center font-semibold text-ink">{cell(allTiers[selected], row.key)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Always included */}
          <div className="mt-4 rounded-2xl border border-line bg-surface px-4 py-3">
            <p className="mb-2 text-[12px] font-semibold uppercase tracking-[0.1em] text-ink-3">
              {t('billing.alwaysIncluded', 'Always included')}
            </p>
            <ul className="space-y-2">
              {ALWAYS_INCLUDED.map((f) => (
                <li key={f.key} className="flex items-center gap-2.5 text-[13.5px] text-ink">
                  <span className="text-accent"><Icon name="check" size={16} /></span>
                  {t(f.key, f.fallback)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      </div>

      {/* ── Sticky CTAs: both billing options ───────────────────────────── */}
      <div className="shrink-0 border-t border-line bg-bg px-5 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-4">
        <div className="mx-auto w-full max-w-md">
          {canOrder ? (
            <div className="space-y-2.5">
              {/* Monthly subscription */}
              <button
                onClick={() => buy(selected, false)}
                disabled={busy !== null}
                className="flex min-h-14 w-full items-center justify-between gap-2 rounded-2xl bg-ink px-5 text-bg disabled:opacity-40"
              >
                <span className="text-left">
                  <span className="block text-[15px] font-semibold">
                    {t('billing.subscribe', 'Subscribe to {plan}').replace('{plan}', selectedName)}
                  </span>
                  <span className="block text-[12px] opacity-80">{t('billing.autoRenew', 'Auto-renews monthly · cancel anytime')}</span>
                </span>
                <span className="shrink-0 text-[15px] font-semibold">
                  {busy === selected ? t('common.loading', 'Loading…') : `${monthlyPriceText}${t('billing.perMo', '/mo')}`}
                </span>
              </button>

              {/* One-time 24-month pass with discount badge */}
              {oneTime && savings && (
                <button
                  onClick={() => buy(selected, true)}
                  disabled={busy !== null}
                  className="relative flex min-h-14 w-full items-center justify-between gap-2 rounded-2xl border-2 border-accent bg-[color-mix(in_oklch,var(--color-accent)_8%,var(--color-bg))] px-5 text-ink disabled:opacity-40"
                >
                  {savings.pct > 0 && (
                    <span className="absolute -top-2.5 right-4 rounded-full bg-[var(--color-ok,#1a7f37)] px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: '#1a7f37' }}>
                      {t('billing.savePct', 'Save {pct}%').replace('{pct}', String(savings.pct))}
                    </span>
                  )}
                  <span className="text-left">
                    <span className="block text-[15px] font-semibold">{t('billing.payOnce', 'Pay once')}</span>
                    <span className="block text-[12px] text-ink-2">
                      {t('billing.oneTimeSub', '{months} months · no renewal').replace('{months}', String(savings.months))}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-[15px] font-semibold">
                      {busy === `${selected}:lt` ? t('common.loading', 'Loading…') : lifetimePriceText}
                    </span>
                    {savingsText && (
                      <span className="block text-[11px] text-ink-3">
                        {t('billing.saveAmount', 'save {sym}{save}').replace('{sym}', '').replace('{save}', savingsText)}
                      </span>
                    )}
                  </span>
                </button>
              )}
            </div>
          ) : (
            <p className="flex min-h-12 items-center justify-center rounded-2xl border border-line text-[14px] font-semibold text-ink-3">
              {selected === currentTier ? t('billing.currentPlan', 'Your current plan') : t('billing.includedIn', 'Included in your plan')}
            </p>
          )}

          {trial?.eligible && currentTier === 'free' && (
            <button
              onClick={() => startTrial.mutate(undefined, { onSuccess: onClose })}
              disabled={startTrial.isPending}
              className="mt-2 block w-full text-center text-[13px] font-semibold text-accent disabled:opacity-50"
            >
              {t('billing.orTrial', 'Or start a 2-week free Family trial')}
            </button>
          )}

          {/* Functional links to Terms of Use and Privacy Policy, required on
              the purchase screen itself per Apple's Schedule 2 §3.8(b) and the
              equivalent Google Play policy — separate from (and in addition
              to) the one-time acceptance gate at signup. */}
          <p className="mt-2 flex justify-center gap-1.5 text-[11px] text-ink-3">
            <a href="https://babynook.app/terms" target="_blank" rel="noopener noreferrer" className="underline">
              {t('billing.termsOfUse', 'Terms of Use')}
            </a>
            <span>·</span>
            <a href="https://babynook.app/privacy" target="_blank" rel="noopener noreferrer" className="underline">
              {t('billing.privacyPolicy', 'Privacy Policy')}
            </a>
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Render a feature value: 0 AI → dash, otherwise the localized number. */
function cell(limits: TierLimits, key: keyof TierLimits): string {
  const v = limits[key];
  if (key === 'activity_ai_weekly' && v === 0) return '—';
  return v.toLocaleString();
}
